import {
  KeycloakCredentialContainer,
  KeycloakDevice,
  KeycloakUserInfo,
} from "core/types"
import {
  USER_CREDENTIALS_URL,
  USER_DEVICES_URL,
  USER_INFO_URL,
  USER_SESSIONS_URL,
} from "./constants"

const jsonHeaders = {
  "Content-Type": "application/json",
}

const getJson = <T>(url: string): Promise<T> =>
  fetch(url, { method: "GET", headers: jsonHeaders }).then((response) => {
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`)
    }
    return response.json() as Promise<T>
  })

const requestDelete = (url: string): Promise<Response> =>
  fetch(url, { method: "DELETE", headers: jsonHeaders }).then((response) => {
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`)
    }
    return response
  })

export const GetAccountInitialData = async (): Promise<{
  userInfo: PromiseSettledResult<KeycloakUserInfo>
  credentials: PromiseSettledResult<KeycloakCredentialContainer[]>
  devices: PromiseSettledResult<KeycloakDevice[]>
}> => {
  const [userInfo, credentials, devices] = await Promise.allSettled([
    getJson<KeycloakUserInfo>(USER_INFO_URL),
    getJson<KeycloakCredentialContainer[]>(USER_CREDENTIALS_URL),
    getJson<KeycloakDevice[]>(USER_DEVICES_URL),
  ])

  return { userInfo, credentials, devices }
}

export const GetDevices = (): Promise<KeycloakDevice[]> =>
  getJson<KeycloakDevice[]>(USER_DEVICES_URL)

export const DeleteSession = (sessionId: string): Promise<Response> =>
  requestDelete(`${USER_SESSIONS_URL}/${sessionId}`)

/**
 * `current` defaults to false server-side, which Keycloak reads as "sign out
 * every session except mine". Pass it explicitly so this session is gone even
 * if the caller's subsequent logout redirect never completes.
 */
export const DeleteAllSessions = (): Promise<Response> =>
  requestDelete(`${USER_SESSIONS_URL}?current=true`)

/**
 * The account API answers a revoked session with a 401 that has no CORS
 * headers, so fetch reports it as a network error, indistinguishable from an
 * outage. Keycloak's userinfo endpoint does send CORS headers on its 401s, so
 * the session's state is readable here. Failing to reach it is an outage, not a
 * revocation.
 */
export const IsSessionRevoked = async (): Promise<boolean> => {
  try {
    const response = await fetch(USER_INFO_URL)
    return response.status === 401
  } catch (error) {
    return false
  }
}
